/**
 * VA-27998 — a recording video source that never ends.
 *
 * WHY THIS EXISTS. The recorder used to be handed the screen-share track
 * directly. When the candidate stops sharing, that track ends — and per the
 * MediaRecorder spec a recorder captures the tracks present when it STARTED,
 * so handing it a fresh track after a re-share does nothing. The consequence
 * was silent and total: a candidate could stop sharing, re-share, complete a
 * forty-minute interview, and the recording would be blank from the moment
 * they stopped, discovered weeks later by whoever opened the report.
 *
 * So the recorder is given a CANVAS track instead. The canvas is drawn from
 * whichever source is currently live, and from a placeholder card when there
 * is none. Its `captureStream` track never ends, which means:
 *
 *   - no gap in the recording,
 *   - no track swap and no recorder restart (which would also collide with
 *     the multipart part-numbering problem on rejoin),
 *   - the interruption becomes visible evidence, with its exact duration,
 *   - almost no bandwidth while held: a still frame is nearly free for VP9.
 *
 * THE RISK THIS CARRIES, and why the knobs below are not decoration. This is
 * the recording hot path, and it has already caused a real loss once: CPU
 * contention starved audio capture and a candidate's answer was never
 * transcribed. Compositing every frame adds work to exactly that path.
 * Therefore the canvas is sized from the ADMIN-CONFIGURED capture width rather
 * than the display's native resolution, the draw rate is capped to the
 * recording frame rate rather than the display's, and while the placeholder is
 * showing the rate drops to a trickle because a still image needs no more.
 *
 * The canvas also keeps FIXED dimensions for the life of the recording. A
 * candidate who re-shares a differently-shaped display is letterboxed rather
 * than changing the encoded resolution mid-stream, which WebM handles poorly.
 */
/** Frames per second while the placeholder card is showing. It is a still
 *  image; anything more is CPU spent to encode the same pixels. */
const PLACEHOLDER_FPS = 2;
/** Fallback geometry when no source has ever been seen. 16:9 at a modest
 *  width — replaced by the real aspect as soon as a source arrives. */
const FALLBACK_WIDTH = 1280;
const FALLBACK_HEIGHT = 720;
/** Text shown on the placeholder card, so a reviewer sees what happened. */
export const PLACEHOLDER_TITLE = 'Screen sharing stopped';
export class ScreenCompositor {
    constructor(opts) {
        var _a;
        this.timer = null;
        this.currentFps = 0;
        this.source = null;
        this.stoppedAt = null;
        this.disposed = false;
        this.maxWidth = Math.max(320, Math.floor(opts.maxWidth) || FALLBACK_WIDTH);
        this.fps = Math.max(1, Math.floor(opts.maxFrameRate) || 15);
        this.now = (_a = opts.now) !== null && _a !== void 0 ? _a : (() => new Date());
        this.canvas = document.createElement('canvas');
        this.canvas.width = Math.min(this.maxWidth, FALLBACK_WIDTH);
        this.canvas.height = Math.round((this.canvas.width * FALLBACK_HEIGHT) / FALLBACK_WIDTH);
        this.ctx = this.canvas.getContext('2d');
        // A detached <video> is the only way to get frames out of a MediaStream
        // for drawImage. Muted + playsInline so it can autoplay without a gesture
        // and never produces sound of its own.
        this.video = document.createElement('video');
        this.video.muted = true;
        this.video.playsInline = true;
        this.outStream = this.canvas.captureStream(this.fps);
        this.paint();
        this.setRate(PLACEHOLDER_FPS);
    }
    /** The never-ending video track to hand the recorder. */
    get track() {
        var _a;
        return (_a = this.outStream.getVideoTracks()[0]) !== null && _a !== void 0 ? _a : null;
    }
    get stream() {
        return this.outStream;
    }
    /** True while the placeholder is showing. */
    get isPlaceholder() {
        return this.source === null;
    }
    /**
     * Point the canvas at a live screen share, or at nothing.
     *
     * Passing `null` is what happens when the candidate stops sharing: the
     * placeholder takes over and the recording continues uninterrupted. Passing
     * a stream again resumes normal drawing — the recorder is never told, and
     * never needs to be.
     */
    setSource(stream) {
        if (this.disposed)
            return;
        this.source = stream;
        if (!stream) {
            this.stoppedAt = this.now();
            try {
                this.video.pause();
            }
            catch (_a) {
                /* not playing */
            }
            this.video.srcObject = null;
            this.setRate(PLACEHOLDER_FPS);
            this.paint();
            return;
        }
        this.stoppedAt = null;
        this.video.srcObject = stream;
        // `play()` returns void on older engines, not a promise — and a rejection
        // here (autoplay policy, a stream that ends between the two statements) is
        // not worth failing the recording over.
        try {
            const started = this.video.play();
            if (started && typeof started.catch === 'function')
                started.catch(() => undefined);
        }
        catch (_b) {
            /* nothing to play yet; the next paint falls back to the card */
        }
        this.setRate(this.fps);
    }
    /** Stop drawing and release the track. Safe to call twice. */
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        if (this.timer !== null)
            clearInterval(this.timer);
        this.timer = null;
        this.video.srcObject = null;
        this.outStream.getTracks().forEach((t) => t.stop());
    }
    // ── internals ─────────────────────────────────────────────────────────────
    setRate(fps) {
        const target = Math.max(1, Math.min(fps, this.fps));
        if (this.timer !== null && this.currentFps === target)
            return;
        if (this.timer !== null)
            clearInterval(this.timer);
        this.currentFps = target;
        this.timer = setInterval(() => this.paint(), Math.round(1000 / target));
    }
    /**
     * Match the canvas to the source's aspect ratio, once, capped at the
     * configured width.
     *
     * Resizing clears the canvas, so it is done only when the shape actually
     * changes — doing it every frame would both waste work and flicker.
     */
    fitTo(vw, vh) {
        if (!vw || !vh)
            return;
        const width = Math.min(this.maxWidth, vw);
        const height = Math.max(1, Math.round((width * vh) / vw));
        if (this.canvas.width === width && this.canvas.height === height)
            return;
        this.canvas.width = width;
        this.canvas.height = height;
    }
    paint() {
        const ctx = this.ctx;
        if (!ctx || this.disposed)
            return;
        if (this.source && this.video.readyState >= 2 && this.video.videoWidth) {
            this.fitTo(this.video.videoWidth, this.video.videoHeight);
            try {
                ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
                return;
            }
            catch (_a) {
                // A frame that is not yet decodable — fall through to the card rather
                // than leaving whatever was last painted, which would be misleading.
            }
        }
        this.paintPlaceholder(ctx);
    }
    paintPlaceholder(ctx) {
        const { width: w, height: h } = this.canvas;
        ctx.fillStyle = '#0b1220';
        ctx.fillRect(0, 0, w, h);
        // Scale the type to the canvas so the card reads the same whether this is
        // a 640px or a 1920px recording.
        const unit = Math.max(12, Math.round(w / 40));
        ctx.fillStyle = '#f87171';
        ctx.textAlign = 'center';
        ctx.font = `bold ${unit * 1.4}px system-ui, -apple-system, sans-serif`;
        ctx.fillText(PLACEHOLDER_TITLE, w / 2, h / 2 - unit * 0.6);
        ctx.fillStyle = '#cbd5e1';
        ctx.font = `${unit * 0.85}px system-ui, -apple-system, sans-serif`;
        if (this.stoppedAt) {
            ctx.fillText(`The candidate stopped sharing at ${this.stoppedAt.toLocaleTimeString()}`, w / 2, h / 2 + unit * 0.9);
        }
        ctx.fillText('The interview was paused while sharing was restored.', w / 2, h / 2 + unit * 2.2);
    }
}
